import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Circle, Marker, Polygon, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { 
  MapPin, Plus, Trash2, Save, Loader2, Navigation, Search, X, 
  GripVertical, Power, Ban, Edit3, AlertTriangle, Check
} from 'lucide-react';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';

import { getBackendUrl } from '../utils/api';

const API = getBackendUrl();

// Fix para ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Ícone customizado para o estabelecimento
const storeIcon = new L.DivIcon({
  className: 'store-marker',
  html: `<div style="
    width: 40px; 
    height: 40px; 
    background: linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%); 
    border-radius: 50%; 
    display: flex; 
    align-items: center; 
    justify-content: center;
    border: 3px solid white;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  ">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

// Cores para as zonas de entrega
const ZONE_COLORS = [
  { fill: 'rgba(59, 130, 246, 0.2)', stroke: '#3B82F6', name: 'Azul' },
  { fill: 'rgba(34, 197, 94, 0.2)', stroke: '#22C55E', name: 'Verde' },
  { fill: 'rgba(234, 179, 8, 0.2)', stroke: '#EAB308', name: 'Amarelo' },
  { fill: 'rgba(239, 68, 68, 0.2)', stroke: '#EF4444', name: 'Vermelho' },
  { fill: 'rgba(168, 85, 247, 0.2)', stroke: '#A855F7', name: 'Roxo' },
];

// Cor para zonas de exclusão
const EXCLUSION_COLOR = {
  fill: 'rgba(239, 68, 68, 0.4)',
  stroke: '#DC2626',
  pattern: true
};

// Componente para atualizar centro do mapa
function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
}

// Componente para permitir clicar no mapa e definir localização
function LocationPicker({ onLocationSelect, enabled }) {
  useMapEvents({
    click: (e) => {
      if (enabled) {
        onLocationSelect(e.latlng);
      }
    },
  });
  return null;
}

// Componente para controles de desenho de polígonos
function DrawControls({ onPolygonCreated, drawingMode, setDrawingMode }) {
  const map = useMap();
  const drawControlRef = useRef(null);
  const drawnItemsRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    // Criar layer para itens desenhados
    drawnItemsRef.current = new L.FeatureGroup();
    map.addLayer(drawnItemsRef.current);

    // Configurar controles de desenho
    drawControlRef.current = new L.Control.Draw({
      position: 'topright',
      draw: {
        polygon: {
          allowIntersection: false,
          shapeOptions: {
            color: EXCLUSION_COLOR.stroke,
            fillColor: EXCLUSION_COLOR.fill,
            fillOpacity: 0.4,
            weight: 2
          }
        },
        polyline: false,
        circle: false,
        rectangle: false,
        marker: false,
        circlemarker: false
      },
      edit: {
        featureGroup: drawnItemsRef.current,
        remove: false
      }
    });

    // Event handlers
    const handleDrawCreated = (e) => {
      const layer = e.layer;
      const coords = layer.getLatLngs()[0].map(ll => [ll.lat, ll.lng]);
      onPolygonCreated(coords);
      setDrawingMode(false);
    };

    map.on(L.Draw.Event.CREATED, handleDrawCreated);

    return () => {
      map.off(L.Draw.Event.CREATED, handleDrawCreated);
      if (drawnItemsRef.current) {
        map.removeLayer(drawnItemsRef.current);
      }
    };
  }, [map, onPolygonCreated, setDrawingMode]);

  // Ativar/desativar modo de desenho
  useEffect(() => {
    if (!map || !drawControlRef.current) return;

    if (drawingMode) {
      map.addControl(drawControlRef.current);
      // Iniciar desenho automaticamente
      new L.Draw.Polygon(map, drawControlRef.current.options.draw.polygon).enable();
    } else {
      try {
        map.removeControl(drawControlRef.current);
      } catch (e) {
        console.warn('DeliveryZonesMap: erro ao remover controle de desenho:', e.message);
      }
    }
  }, [map, drawingMode]);

  return null;
}

/**
 * DeliveryZonesMap - Componente de configuração de zonas de entrega com mapa
 * Agora com suporte a zonas de exclusão (polígonos)
 */
const DeliveryZonesMap = ({ 
  restaurantId, 
  initialLocation = null,
  initialZones = [],
  initialExclusionZones = [],
  onSave,
  onLocationChange 
}) => {
  // Estados principais
  const [location, setLocation] = useState(initialLocation);
  const [zones, setZones] = useState(initialZones.length > 0 ? initialZones : [
    { id: 1, name: 'Zona 1', radius: 2, fee: 5, time: 20, active: true },
    { id: 2, name: 'Zona 2', radius: 5, fee: 8, time: 30, active: true },
    { id: 3, name: 'Zona 3', radius: 8, fee: 12, time: 45, active: false },
  ]);
  
  // Estados para zonas de exclusão
  const [exclusionZones, setExclusionZones] = useState(initialExclusionZones);
  const [drawingMode, setDrawingMode] = useState(false);
  const [editingExclusion, setEditingExclusion] = useState(null);
  const [newExclusionName, setNewExclusionName] = useState('');
  const [newExclusionReason, setNewExclusionReason] = useState('');
  const [showExclusionPanel, setShowExclusionPanel] = useState(false);
  const [pendingPolygon, setPendingPolygon] = useState(null);
  
  // Estados gerais
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pickingLocation, setPickingLocation] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('zones'); // 'zones' ou 'exclusions'
  
  const searchTimeoutRef = useRef(null);

  // Carregar configuração existente
  useEffect(() => {
    const loadConfig = async () => {
      if (!restaurantId) return;
      
      try {
        const response = await axios.get(`${API}/api/admin/delivery-config/${restaurantId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (response.data.success) {
          // Carregar localização
          if (response.data.location) {
            setLocation(response.data.location);
          }
          
          // Carregar zonas de entrega
          if (response.data.config?.radius_zones) {
            const loadedZones = response.data.config.radius_zones.map((z, idx) => ({
              id: idx + 1,
              name: z.name || `Zona ${idx + 1}`,
              radius: z.max_distance_km || z.radius_km || 5,
              fee: z.fee || 0,
              time: z.time_minutes || 30,
              active: true
            }));
            if (loadedZones.length > 0) {
              setZones(loadedZones);
            }
          }
          
          // Carregar zonas de exclusão
          if (response.data.config?.exclusion_zones) {
            setExclusionZones(response.data.config.exclusion_zones);
          }
        }
      } catch (err) {
        console.error('Erro ao carregar configuração:', err);
      }
    };
    
    loadConfig();
  }, [restaurantId]);

  // Buscar endereço
  const searchAddress = useCallback(async (query) => {
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/api/address/autocomplete`, {
        params: { q: query, limit: 5 }
      });
      setSearchResults(response.data.suggestions || []);
    } catch (err) {
      console.error('Erro na busca:', err);
    } finally {
      setSearching(false);
    }
  }, []);

  // Handler de busca com debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(value);
    }, 400);
  };

  // Selecionar resultado da busca
  const handleSelectSearchResult = (result) => {
    const newLocation = { lat: result.lat, lng: result.lng };
    setLocation(newLocation);
    setSearchQuery(result.main_text || '');
    setSearchResults([]);
    onLocationChange?.(newLocation);
    setPickingLocation(false);
    showMessage('Localização definida! Ajuste os raios das zonas.', 'success');
  };

  // Usar localização atual (GPS)
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      showMessage('Geolocalização não suportada', 'error');
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setLocation(newLocation);
        onLocationChange?.(newLocation);
        showMessage('Localização definida via GPS!', 'success');
      },
      (err) => {
        showMessage('Erro ao obter localização: ' + err.message, 'error');
      }
    );
  };

  // Clicar no mapa para definir localização
  const handleMapClick = (latlng) => {
    if (pickingLocation) {
      setLocation({ lat: latlng.lat, lng: latlng.lng });
      onLocationChange?.({ lat: latlng.lat, lng: latlng.lng });
      setPickingLocation(false);
      showMessage('Localização definida no mapa!', 'success');
    }
  };

  // ========== ZONAS DE ENTREGA ==========
  
  const addZone = () => {
    const maxId = Math.max(...zones.map(z => z.id), 0);
    const maxRadius = Math.max(...zones.map(z => z.radius), 0);
    
    setZones([...zones, {
      id: maxId + 1,
      name: `Zona ${maxId + 1}`,
      radius: maxRadius + 3,
      fee: 10,
      time: 40,
      active: true
    }]);
  };

  const removeZone = (id) => {
    if (zones.length <= 1) {
      showMessage('Deve haver pelo menos uma zona', 'error');
      return;
    }
    setZones(zones.filter(z => z.id !== id));
  };

  const updateZone = (id, field, value) => {
    setZones(zones.map(z => 
      z.id === id ? { ...z, [field]: value } : z
    ));
  };

  // ========== ZONAS DE EXCLUSÃO ==========
  
  const handlePolygonCreated = (coords) => {
    setPendingPolygon(coords);
    setNewExclusionName('');
    setNewExclusionReason('');
  };

  const saveNewExclusion = () => {
    if (!pendingPolygon || !newExclusionName.trim()) {
      showMessage('Informe um nome para a zona de exclusão', 'error');
      return;
    }

    const newExclusion = {
      id: `exc_${Date.now()}`,
      name: newExclusionName.trim(),
      reason: newExclusionReason.trim(),
      polygon: pendingPolygon,
      created_at: new Date().toISOString()
    };

    setExclusionZones([...exclusionZones, newExclusion]);
    setPendingPolygon(null);
    setNewExclusionName('');
    setNewExclusionReason('');
    showMessage(`Zona "${newExclusion.name}" adicionada!`, 'success');
  };

  const cancelNewExclusion = () => {
    setPendingPolygon(null);
    setNewExclusionName('');
    setNewExclusionReason('');
    setDrawingMode(false);
  };

  const removeExclusion = (id) => {
    setExclusionZones(exclusionZones.filter(ez => ez.id !== id));
    showMessage('Zona de exclusão removida', 'success');
  };

  const updateExclusion = (id, field, value) => {
    setExclusionZones(exclusionZones.map(ez =>
      ez.id === id ? { ...ez, [field]: value } : ez
    ));
  };

  // ========== SALVAR ==========
  
  const handleSave = async () => {
    if (!location) {
      showMessage('Defina a localização do estabelecimento primeiro', 'error');
      return;
    }
    
    setSaving(true);
    try {
      const configData = {
        restaurant_id: restaurantId,
        mode: 'radius',
        latitude: location.lat,
        longitude: location.lng,
        radius_zones: zones
          .filter(z => z.active)
          .sort((a, b) => a.radius - b.radius)
          .map(z => ({
            name: z.name,
            max_distance_km: z.radius,
            fee: z.fee,
            time_minutes: z.time
          })),
        exclusion_zones: exclusionZones.map(ez => ({
          id: ez.id,
          name: ez.name,
          reason: ez.reason,
          polygon: ez.polygon,
          created_at: ez.created_at
        }))
      };
      
      await axios.post(`${API}/api/admin/delivery-config`, configData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      showMessage('Configurações salvas com sucesso!', 'success');
      onSave?.(configData);
    } catch (err) {
      console.error('Erro ao salvar:', err);
      showMessage('Erro ao salvar configurações', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Mostrar mensagem temporária
  const showMessage = (text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  // Centro do mapa
  const mapCenter = location ? [location.lat, location.lng] : [-15.7801, -47.9292];

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <MapPin size={24} />
          Configurar Zonas de Entrega
        </h2>
        <p className="text-blue-100 text-sm mt-1">
          Configure os raios de entrega e áreas de exclusão
        </p>
      </div>

      {/* Mensagem de feedback */}
      {message && (
        <div className={`p-3 text-sm font-medium ${
          message.type === 'success' ? 'bg-green-50 text-green-700' :
          message.type === 'error' ? 'bg-red-50 text-red-700' :
          'bg-blue-50 text-blue-700'
        }`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b">
        <div className="flex">
          <button
            onClick={() => setActiveTab('zones')}
            className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'zones'
                ? 'border-blue-600 text-blue-600 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <MapPin size={18} />
              Zonas de Entrega
            </div>
          </button>
          <button
            onClick={() => setActiveTab('exclusions')}
            className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'exclusions'
                ? 'border-red-600 text-red-600 bg-red-50'
                : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Ban size={18} />
              Zonas de Exclusão
              {exclusionZones.length > 0 && (
                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {exclusionZones.length}
                </span>
              )}
            </div>
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Mapa */}
        <div className="lg:w-2/3 h-[400px] lg:h-[600px] relative">
          {/* Barra de busca */}
          <div className="absolute top-4 left-4 right-4 z-[1000]">
            <div className="bg-white rounded-lg shadow-lg p-2">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Buscar endereço do estabelecimento..."
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {searching && (
                    <Loader2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
                  )}
                </div>
                <button
                  onClick={handleUseCurrentLocation}
                  className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                  title="Usar minha localização"
                >
                  <Navigation size={18} />
                </button>
                <button
                  onClick={() => setPickingLocation(!pickingLocation)}
                  className={`px-3 py-2 rounded-lg transition-colors ${
                    pickingLocation 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Clicar no mapa para definir"
                >
                  <MapPin size={18} />
                </button>
              </div>
              
              {/* Resultados da busca */}
              {searchResults.length > 0 && (
                <div className="mt-2 border rounded-lg overflow-hidden">
                  {searchResults.map((result, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSelectSearchResult(result)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b last:border-b-0 flex items-start gap-2"
                    >
                      <MapPin size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-gray-900">{result.main_text}</p>
                        <p className="text-sm text-gray-500">{result.secondary_text}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {pickingLocation && (
              <div className="mt-2 bg-green-500 text-white text-center py-2 rounded-lg text-sm font-medium">
                Clique no mapa para definir a localização
              </div>
            )}
            
            {drawingMode && (
              <div className="mt-2 bg-red-500 text-white text-center py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2">
                <Ban size={18} />
                Desenhando zona de exclusão - Clique para criar os vértices, depois clique no primeiro ponto para fechar
              </div>
            )}
          </div>

          {/* Mapa Leaflet */}
          <MapContainer
            center={mapCenter}
            zoom={location ? 13 : 4}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <MapUpdater center={location ? [location.lat, location.lng] : null} />
            <LocationPicker onLocationSelect={handleMapClick} enabled={pickingLocation} />
            <DrawControls 
              onPolygonCreated={handlePolygonCreated} 
              drawingMode={drawingMode}
              setDrawingMode={setDrawingMode}
            />
            
            {/* Zonas de entrega (círculos) */}
            {location && zones
              .filter(z => z.active)
              .sort((a, b) => b.radius - a.radius)
              .map((zone, idx) => {
                const colorIdx = zones.findIndex(z => z.id === zone.id) % ZONE_COLORS.length;
                const color = ZONE_COLORS[colorIdx];
                return (
                  <Circle
                    key={zone.id}
                    center={[location.lat, location.lng]}
                    radius={zone.radius * 1000}
                    pathOptions={{
                      fillColor: color.fill,
                      fillOpacity: 0.3,
                      color: color.stroke,
                      weight: 2,
                    }}
                  />
                );
              })}
            
            {/* Zonas de exclusão (polígonos) */}
            {exclusionZones.map((ez) => (
              <Polygon
                key={ez.id}
                positions={ez.polygon}
                pathOptions={{
                  fillColor: EXCLUSION_COLOR.fill,
                  fillOpacity: 0.5,
                  color: EXCLUSION_COLOR.stroke,
                  weight: 3,
                  dashArray: '5, 10'
                }}
              />
            ))}
            
            {/* Polígono pendente (sendo criado) */}
            {pendingPolygon && (
              <Polygon
                positions={pendingPolygon}
                pathOptions={{
                  fillColor: 'rgba(251, 146, 60, 0.5)',
                  fillOpacity: 0.5,
                  color: '#F97316',
                  weight: 3,
                  dashArray: '5, 5'
                }}
              />
            )}
            
            {/* Marcador do estabelecimento */}
            {location && (
              <Marker position={[location.lat, location.lng]} icon={storeIcon} />
            )}
          </MapContainer>

          {/* Legenda */}
          {location && (
            <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 z-[1000] max-w-xs">
              <p className="text-xs font-medium text-gray-600 mb-2">Legenda:</p>
              <div className="space-y-1">
                {zones.filter(z => z.active).map((zone, idx) => {
                  const colorIdx = zones.findIndex(z => z.id === zone.id) % ZONE_COLORS.length;
                  return (
                    <div key={zone.id} className="flex items-center gap-2 text-xs">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: ZONE_COLORS[colorIdx].stroke }}
                      />
                      <span>{zone.name}: até {zone.radius}km - R$ {zone.fee}</span>
                    </div>
                  );
                })}
                {exclusionZones.length > 0 && (
                  <div className="flex items-center gap-2 text-xs pt-1 border-t mt-1">
                    <div 
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: EXCLUSION_COLOR.stroke }}
                    />
                    <span className="text-red-600 font-medium">
                      {exclusionZones.length} zona(s) bloqueada(s)
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Painel lateral */}
        <div className="lg:w-1/3 p-4 bg-gray-50 border-l max-h-[600px] overflow-y-auto">
          
          {/* CONTEÚDO DA TAB ZONAS */}
          {activeTab === 'zones' && (
            <>
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <GripVertical size={20} />
                Zonas de Entrega
              </h3>

              {!location && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700">
                    <strong>Primeiro:</strong> Defina a localização do seu estabelecimento usando a busca ou clicando no mapa.
                  </p>
                </div>
              )}

              {/* Lista de zonas */}
              <div className="space-y-3 mb-4">
                {zones.map((zone, idx) => {
                  const colorIdx = idx % ZONE_COLORS.length;
                  const color = ZONE_COLORS[colorIdx];
                  
                  return (
                    <div 
                      key={zone.id}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        zone.active 
                          ? 'bg-white border-l-4'
                          : 'bg-gray-100 opacity-60'
                      }`}
                      style={{ borderLeftColor: zone.active ? color.stroke : '#9CA3AF' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: color.stroke }}
                          />
                          <input
                            type="text"
                            value={zone.name}
                            onChange={(e) => updateZone(zone.id, 'name', e.target.value)}
                            className="font-medium text-gray-800 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none px-1"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => updateZone(zone.id, 'active', !zone.active)}
                            className={`p-1 rounded transition-colors ${
                              zone.active 
                                ? 'text-green-600 hover:bg-green-100' 
                                : 'text-gray-400 hover:bg-gray-200'
                            }`}
                            title={zone.active ? 'Desativar' : 'Ativar'}
                          >
                            <Power size={16} />
                          </button>
                          <button
                            onClick={() => removeZone(zone.id)}
                            className="p-1 text-red-500 hover:bg-red-100 rounded transition-colors"
                            title="Remover"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Raio (km)</label>
                          <input
                            type="number"
                            value={zone.radius}
                            onChange={(e) => updateZone(zone.id, 'radius', parseFloat(e.target.value) || 0)}
                            min="0.5"
                            step="0.5"
                            className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                            disabled={!zone.active}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Taxa (R$)</label>
                          <input
                            type="number"
                            value={zone.fee}
                            onChange={(e) => updateZone(zone.id, 'fee', parseFloat(e.target.value) || 0)}
                            min="0"
                            step="0.5"
                            className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                            disabled={!zone.active}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Tempo (min)</label>
                          <input
                            type="number"
                            value={zone.time}
                            onChange={(e) => updateZone(zone.id, 'time', parseInt(e.target.value) || 0)}
                            min="5"
                            step="5"
                            className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                            disabled={!zone.active}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={addZone}
                className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
              >
                <Plus size={18} />
                Adicionar Nova Zona
              </button>
            </>
          )}

          {/* CONTEÚDO DA TAB EXCLUSÕES */}
          {activeTab === 'exclusions' && (
            <>
              <h3 className="text-lg font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Ban size={20} className="text-red-500" />
                Zonas de Exclusão
              </h3>
              
              <p className="text-sm text-gray-600 mb-4">
                Áreas onde você <strong>não realiza entregas</strong> (morros, zonas de risco, etc.)
              </p>

              {!location && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700">
                    <strong>Primeiro:</strong> Defina a localização do estabelecimento na aba &quot;Zonas de Entrega&quot;.
                  </p>
                </div>
              )}

              {/* Formulário de nova exclusão (quando há polígono pendente) */}
              {pendingPolygon && (
                <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <h4 className="font-medium text-orange-800 mb-3 flex items-center gap-2">
                    <AlertTriangle size={18} />
                    Nova Zona de Exclusão
                  </h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-700 block mb-1">Nome da área *</label>
                      <input
                        type="text"
                        value={newExclusionName}
                        onChange={(e) => setNewExclusionName(e.target.value)}
                        placeholder="Ex: Morro do Alemão"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm text-gray-700 block mb-1">Motivo (opcional)</label>
                      <input
                        type="text"
                        value={newExclusionReason}
                        onChange={(e) => setNewExclusionReason(e.target.value)}
                        placeholder="Ex: Área de difícil acesso"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={saveNewExclusion}
                        disabled={!newExclusionName.trim()}
                        className="flex-1 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        <Check size={18} />
                        Salvar
                      </button>
                      <button
                        onClick={cancelNewExclusion}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Botão para desenhar nova exclusão */}
              {!pendingPolygon && location && (
                <button
                  onClick={() => setDrawingMode(true)}
                  disabled={drawingMode}
                  className={`w-full mb-4 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                    drawingMode
                      ? 'bg-red-100 text-red-700 cursor-not-allowed'
                      : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                >
                  <Edit3 size={18} />
                  {drawingMode ? 'Desenhando...' : 'Desenhar Nova Zona de Exclusão'}
                </button>
              )}

              {/* Lista de exclusões existentes */}
              {exclusionZones.length > 0 ? (
                <div className="space-y-3">
                  {exclusionZones.map((ez) => (
                    <div 
                      key={ez.id}
                      className="p-3 bg-red-50 border border-red-200 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Ban size={16} className="text-red-500" />
                            <span className="font-medium text-red-800">{ez.name}</span>
                          </div>
                          {ez.reason && (
                            <p className="text-sm text-red-600 mt-1 ml-6">{ez.reason}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1 ml-6">
                            {ez.polygon?.length || 0} vértices
                          </p>
                        </div>
                        <button
                          onClick={() => removeExclusion(ez.id)}
                          className="p-1.5 text-red-500 hover:bg-red-100 rounded transition-colors"
                          title="Remover zona"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Ban size={40} className="mx-auto mb-3 text-gray-300" />
                  <p>Nenhuma zona de exclusão definida</p>
                  <p className="text-sm mt-1">Todas as áreas dentro do raio de entrega estão liberadas</p>
                </div>
              )}
            </>
          )}

          {/* Botão salvar (sempre visível) */}
          <button
            onClick={handleSave}
            disabled={saving || !location}
            className="w-full mt-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save size={18} />
                Salvar Todas as Configurações
              </>
            )}
          </button>

          {location && (
            <p className="mt-3 text-xs text-gray-500 text-center">
              Lat: {location.lat.toFixed(6)}, Lng: {location.lng.toFixed(6)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryZonesMap;
